import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * 下からフェードイン（ScrollTrigger）
 *
 * 親に js-scroll-fade-up、動かす要素に js-scroll-fade-up-target を付与。
 * 親の data 属性で調整（省略時はデフォルト）:
 *   data-scroll-fade-up-stagger  例: 0.1
 *   data-scroll-fade-up-y         例: 28
 *   data-scroll-fade-up-duration  例: 0.65
 *   data-scroll-fade-up-start     例: top 85%
 *
 * 1文字ずつ下から表示（ScrollTrigger）
 * 要素に js-scroll-text-chars を付与（子の HTML は維持しテキストのみ分割）
 * 発火トリガーは .common-section-heading（なければ .left-item → 要素自身）
 *   data-scroll-text-chars-stagger   例: 0.04（1文字間。長文は max-spread で全体上限）
 *   data-scroll-text-chars-max-spread  文字間の合計の上限秒 例: 1.2
 *   data-scroll-text-chars-y         例: 24
 *   data-scroll-text-chars-duration  例: 0.5
 *   data-scroll-text-chars-start     例: top 85%
 */
const SCROLL_FADE_UP_DEFAULTS = {
  stagger: 0.1,
  y: 28,
  duration: 0.65,
  start: 'top 85%',
  ease: 'power2.out',
};

function readScrollFadeUpOptions(root) {
  const { dataset } = root;

  return {
    stagger: dataset.scrollFadeUpStagger !== undefined
      ? Number(dataset.scrollFadeUpStagger)
      : SCROLL_FADE_UP_DEFAULTS.stagger,
    y: dataset.scrollFadeUpY !== undefined
      ? Number(dataset.scrollFadeUpY)
      : SCROLL_FADE_UP_DEFAULTS.y,
    duration: dataset.scrollFadeUpDuration !== undefined
      ? Number(dataset.scrollFadeUpDuration)
      : SCROLL_FADE_UP_DEFAULTS.duration,
    start: dataset.scrollFadeUpStart ?? SCROLL_FADE_UP_DEFAULTS.start,
    ease: SCROLL_FADE_UP_DEFAULTS.ease,
  };
}

const SCROLL_TEXT_CHARS_DEFAULTS = {
  stagger: 0.04,
  maxSpread: 1.2,
  y: 20,
  duration: 0.35,
  start: 'top 85%',
  ease: 'power2.out',
};

function readScrollTextCharsOptions(element) {
  const { dataset } = element;

  return {
    stagger: dataset.scrollTextCharsStagger !== undefined
      ? Number(dataset.scrollTextCharsStagger)
      : SCROLL_TEXT_CHARS_DEFAULTS.stagger,
    maxSpread: dataset.scrollTextCharsMaxSpread !== undefined
      ? Number(dataset.scrollTextCharsMaxSpread)
      : SCROLL_TEXT_CHARS_DEFAULTS.maxSpread,
    y: dataset.scrollTextCharsY !== undefined
      ? Number(dataset.scrollTextCharsY)
      : SCROLL_TEXT_CHARS_DEFAULTS.y,
    duration: dataset.scrollTextCharsDuration !== undefined
      ? Number(dataset.scrollTextCharsDuration)
      : SCROLL_TEXT_CHARS_DEFAULTS.duration,
    start: dataset.scrollTextCharsStart ?? SCROLL_TEXT_CHARS_DEFAULTS.start,
    ease: SCROLL_TEXT_CHARS_DEFAULTS.ease,
  };
}

function getCharStagger(charCount, stagger, maxSpread) {
  if (charCount <= 1) {
    return stagger;
  }

  const spreadCap = maxSpread / (charCount - 1);

  return Math.min(stagger, spreadCap);
}

function normalizeTextContent(text) {
  return text
    .replace(/[ \t]*[\r\n]+[ \t]*/g, '')
    .replace(/^\s+|\s+$/g, '');
}

function splitTextToCharSpans(element) {
  const charSpans = [];
  const textNodes = [];

  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT
  );

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach((textNode) => {
    const textContent = normalizeTextContent(textNode.textContent || '');

    if (!textContent || !textNode.parentNode) {
      textNode.parentNode?.removeChild(textNode);
      return;
    }

    const fragment = document.createDocumentFragment();

    [...textContent].forEach((char) => {
      if (char === ' ') {
        fragment.appendChild(document.createTextNode(char));
        return;
      }

      const span = document.createElement('span');
      span.className = 'js-scroll-text-char';
      span.textContent = char;
      fragment.appendChild(span);
      charSpans.push(span);
    });

    textNode.parentNode.replaceChild(fragment, textNode);
  });

  return charSpans;
}

function getTextCharsTrigger(element) {
  return (
    element.closest('.common-section-heading')
    || element.closest('.left-item')
    || element
  );
}

function initScrollTextChars() {
  const elements = document.querySelectorAll('.js-scroll-text-chars');

  elements.forEach((element) => {
    const chars = splitTextToCharSpans(element);

    if (!chars.length) {
      return;
    }

    const options = readScrollTextCharsOptions(element);
    const stagger = getCharStagger(chars.length, options.stagger, options.maxSpread);
    const trigger = getTextCharsTrigger(element);

    gsap.set(chars, { opacity: 0, y: options.y });
    gsap.set(element, { opacity: 1 });

    gsap.to(chars, {
      opacity: 1,
      y: 0,
      duration: options.duration,
      ease: options.ease,
      stagger,
      clearProps: 'transform',
      scrollTrigger: {
        trigger,
        start: options.start,
        once: true,
        toggleActions: 'play none none none',
      },
    });
  });
}

function initScrollFadeUp() {
  const roots = document.querySelectorAll('.js-scroll-fade-up');

  roots.forEach((root) => {
    const targets = root.querySelectorAll('.js-scroll-fade-up-target');

    if (!targets.length) {
      return;
    }

    const options = readScrollFadeUpOptions(root);

    gsap.set(targets, { opacity: 0, y: options.y });

    gsap.to(targets, {
      opacity: 1,
      y: 0,
      duration: options.duration,
      ease: options.ease,
      stagger: options.stagger,
      clearProps: 'transform',
      scrollTrigger: {
        trigger: root,
        start: options.start,
        once: true,
        toggleActions: 'play none none none',
      },
    });
  });
}

export function refreshScrollTrigger() {
  ScrollTrigger.refresh();
}

export function initScrollTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  initScrollTextChars();
  initScrollFadeUp();

  ScrollTrigger.refresh();

  window.addEventListener('load', () => {
    ScrollTrigger.refresh();
  });
}
